import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';
import { useData } from './DataContext';

export const INVOICE_STATUSES = [
  { id: 'draft', label: 'draft', color: '#94a3b8' },
  { id: 'sent', label: 'sent', color: '#38bdf8' },
  { id: 'paid', label: 'paid', color: '#059669' },
  { id: 'overdue', label: 'overdue', color: '#ef4444' },
];

const InvoicesContext = createContext(null);

export const InvoicesProvider = ({ children }) => {
  const { user } = useAuth();
  const { talent, brands, talentMap, brandMap } = useData();
  const [invoices, setInvoices] = useState([]);
  const [sender, setSenderState] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setInvoices([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [inv, snd] = await Promise.all([
        api.get('/invoices'),
        api.get('/sender'),
      ]);
      setInvoices(inv.data || []);
      setSenderState(snd.data || {});
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const nextNumber = async () => {
    try {
      const { data } = await api.get('/invoices/next-number');
      return data.number;
    } catch {
      // Fallback: local best-guess
      const nums = invoices.map((i) => parseInt(String(i.number).replace(/\D/g, ''), 10) || 0);
      return `INV-${(Math.max(0, ...nums) + 1).toString().padStart(4, '0')}`;
    }
  };

  const addInvoice = async (payload) => {
    const clean = { ...payload };
    // convert camelCase to snake_case for API
    const apiPayload = {
      number: clean.number, status: clean.status,
      brand_id: clean.brandId || clean.brand_id || null,
      talent_id: clean.talentId || clean.talent_id || null,
      deal_id: clean.dealId || clean.deal_id || null,
      issue_date: clean.issueDate || clean.issue_date || null,
      due_date: clean.dueDate || clean.due_date || null,
      line_items: (clean.lineItems || clean.line_items || []).map((li) => ({
        id: li.id, description: li.description || '',
        quantity: Number(li.quantity) || 0,
        unit_price: Number(li.unitPrice ?? li.unit_price) || 0,
      })),
      tax_rate: Number(clean.taxRate ?? clean.tax_rate) || 0,
      currency: clean.currency || 'GBP',
      notes: clean.notes || '',
      bill_to: clean.billTo || clean.bill_to || '',
      po_number: clean.poNumber || clean.po_number || '',
      bank_account_id: clean.bankAccountId || clean.bank_account_id || null,
    };
    const { data } = await api.post('/invoices', apiPayload);
    setInvoices((prev) => [data, ...prev]);
    return data.id;
  };

  const updateInvoice = async (id, patch) => {
    const apiPatch = { ...patch };
    if ('brandId' in apiPatch) { apiPatch.brand_id = apiPatch.brandId; delete apiPatch.brandId; }
    if ('talentId' in apiPatch) { apiPatch.talent_id = apiPatch.talentId; delete apiPatch.talentId; }
    if ('dealId' in apiPatch) { apiPatch.deal_id = apiPatch.dealId; delete apiPatch.dealId; }
    if ('issueDate' in apiPatch) { apiPatch.issue_date = apiPatch.issueDate; delete apiPatch.issueDate; }
    if ('dueDate' in apiPatch) { apiPatch.due_date = apiPatch.dueDate; delete apiPatch.dueDate; }
    if ('paidDate' in apiPatch) { apiPatch.paid_date = apiPatch.paidDate; delete apiPatch.paidDate; }
    if ('taxRate' in apiPatch) { apiPatch.tax_rate = apiPatch.taxRate; delete apiPatch.taxRate; }
    if ('lineItems' in apiPatch) {
      apiPatch.line_items = apiPatch.lineItems.map((li) => ({ id: li.id, description: li.description || '', quantity: Number(li.quantity) || 0, unit_price: Number(li.unitPrice ?? li.unit_price) || 0 }));
      delete apiPatch.lineItems;
    }
    const { data } = await api.patch(`/invoices/${id}`, apiPatch);
    setInvoices((prev) => prev.map((i) => (i.id === id ? data : i)));
  };

  const removeInvoice = async (id) => {
    await api.delete(`/invoices/${id}`);
    setInvoices((prev) => prev.filter((i) => i.id !== id));
  };

  const setSender = async (payload) => {
    const { data } = await api.put('/sender', payload);
    setSenderState(data);
  };

  return (
    <InvoicesContext.Provider value={{
      loading, invoices, sender, brandMap, talentMap, brands, talent,
      statuses: INVOICE_STATUSES, addInvoice, updateInvoice, removeInvoice, setSender, nextNumber, refresh,
    }}>
      {children}
    </InvoicesContext.Provider>
  );
};

export const useInvoices = () => {
  const c = useContext(InvoicesContext);
  if (!c) throw new Error('useInvoices must be used within InvoicesProvider');
  return c;
};

export const calcTotals = (inv) => {
  const items = inv?.line_items || inv?.lineItems || [];
  const subtotal = items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price ?? li.unitPrice) || 0), 0);
  const taxRate = Number(inv?.tax_rate ?? inv?.taxRate) || 0;
  const tax = subtotal * (taxRate / 100);
  return { subtotal, tax, total: subtotal + tax };
};
